import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomTextField from "../../../components/CustomTextField";
import Select from "../../../components/Select/Select";
import { getStockFastSlowMoving } from "../../../redux/reducers/clinicSlice";
import { isActiveRecord } from "../../../util/activeStatus";
import { AppColors } from "../../../util/AppColors";
import { tblGrayBack } from "../../../util/object";

const durationOptions = [
  { name: "30 days", value: "0-30", qtyKey: "qty30" },
  { name: "60 days", value: "0-60", qtyKey: "qty60" },
  { name: "90 days", value: "0-90", qtyKey: "qty90" },
];

const movementOptions = [
  { name: "Fast Moving", value: "fast" },
  { name: "Slow Moving", value: "slow" },
];

const locationOptions = [
  { name: "Pharmacy", value: "pharmacy" },
  { name: "Pet Shop", value: "petshop" },
];

const initFilters = {
  tradeName: "",
  movement: movementOptions[0],
  duration: durationOptions[0],
  category: null,
  location: null,
};

const displayCell = (value) =>
  value == null || value === "" ? "-" : String(value);

const getMovementRows = (payload, movement) => {
  const body =
    payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const key = movement === "slow" ? "slowMoving" : "fastMoving";
  const list = body?.[key];
  return Array.isArray(list) ? list : [];
};

const qtyForSelection = (row, location, qtyKey) => {
  const key = qtyKey || "qty30";
  const useLocation = location === "pharmacy" || location === "petshop";
  const source = useLocation ? row?.[location] : row;
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return null;
  }
  if (source[key] != null && source[key] !== "") return source[key];
  if (key !== "qty30" && source.qty30 != null && source.qty30 !== "") {
    return source.qty30;
  }
  return null;
};

const FastSlowMoving = () => {
  const dispatch = useDispatch();
  const requestRef = useRef(0);
  const [filters, setFilters] = useState(initFilters);
  const [debouncedTradeName, setDebouncedTradeName] = useState("");
  const [payload, setPayload] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const mandateDataRaw = sessionStorage.getItem("mandateData");

  const stockCategories = useMemo(() => {
    if (!mandateDataRaw) return [];
    try {
      const reqData = JSON.parse(mandateDataRaw);
      const categories = Array.isArray(reqData?.categories)
        ? reqData.categories
        : [];
      return categories
        .filter(
          (cat) => cat?.type === "stock" && cat?.name && isActiveRecord(cat),
        )
        .map((cat) => ({
          name: cat.name,
          value: cat.name,
          id: cat.id,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch {
      return [];
    }
  }, [mandateDataRaw]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedTradeName(String(filters?.tradeName ?? "").trim());
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [filters?.tradeName]);

  useEffect(() => {
    const requestId = ++requestRef.current;
    setLoaded(false);
    dispatch(
      getStockFastSlowMoving({
        movement: filters?.movement?.value ?? "fast",
        days: filters?.duration?.value ?? "0-30",
        tradeName: debouncedTradeName,
        category: filters?.category?.value ?? "",
      }),
    ).then((res) => {
      if (requestId !== requestRef.current) return;
      setPayload(res?.payload ?? null);
      setLoaded(true);
    });
  }, [
    filters?.movement,
    filters?.duration,
    filters?.category,
    debouncedTradeName,
    dispatch,
  ]);

  const rows = useMemo(() => {
    const qtyKey = filters?.duration?.qtyKey || "qty30";
    const location = filters?.location?.value ?? null;
    return getMovementRows(payload, filters?.movement?.value ?? "fast")?.map(
      (row) => ({
        ...row,
        qtySold: qtyForSelection(row, location, qtyKey),
      }),
    );
  }, [payload, filters?.movement, filters?.duration, filters?.location]);

  const onChangeSelect = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value ?? null }));
  };

  const onReset = () => {
    setFilters(initFilters);
  };

  return (
    <Container maxWidth="xl">
      <div className="ph20 mt15">
        <div
          className="flex-row-ali-cen flex-center"
          style={{ flexWrap: "wrap", gap: 12 }}
        >
          <div className="w15Per">
            <CustomTextField
              label="Trade Name"
              placeholder="Trade Name"
              fullWidth
              name="tradeName"
              value={filters?.tradeName ?? ""}
              handleChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  tradeName: e?.target?.value ?? "",
                }))
              }
              backgroundColor={AppColors.lightBlue2}
            />
          </div>
          <div className="w18Per">
            <Select
              list={stockCategories}
              value={filters?.category}
              handleChange={(e, value) => onChangeSelect("category", value)}
              label="Category"
              search
              backgroundColor={AppColors.lightBlue2}
            />
          </div>
          <div className="w15Per">
            <Select
              list={locationOptions}
              value={filters?.location}
              handleChange={(e, value) => onChangeSelect("location", value)}
              label="Location"
              search
              backgroundColor={AppColors.lightBlue2}
            />
          </div>
          <div className="w18Per">
            <Select
              list={durationOptions}
              value={filters?.duration}
              handleChange={(e, value) => onChangeSelect("duration", value)}
              label="Duration"
              search
              backgroundColor={AppColors.lightBlue2}
            />
          </div>
          <div className="w15Per">
            <Select
              list={movementOptions}
              value={filters?.movement}
              handleChange={(e, value) => onChangeSelect("movement", value)}
              label="Type"
              search
              backgroundColor={AppColors.lightBlue2}
            />
          </div>
          <div>
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
        </div>
        <Typography className="font-medium fs12 gray28 mt10">
          Number of Stocks: {loaded ? rows.length : 0}
        </Typography>
        <TableContainer sx={{ maxHeight: "55vh" }} className="mt10">
          <Table aria-label="fast slow moving" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={tblGrayBack} className="txt-semi-bold fs12">
                  Trade Name
                </TableCell>
                <TableCell style={tblGrayBack} className="txt-semi-bold fs12">
                  Category
                </TableCell>
                <TableCell style={tblGrayBack} className="txt-semi-bold fs12">
                  Qty Sold
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loaded && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="h100">
                    <div className="no-rec">No records available</div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => (
                  <TableRow
                    key={`${row?.stockId ?? row?.tradeName ?? "stock"}_${index}`}
                  >
                    <TableCell className="fs12">
                      {displayCell(row?.tradeName)}
                    </TableCell>
                    <TableCell className="fs12">
                      {displayCell(row?.category)}
                    </TableCell>
                    <TableCell className="fs12">
                      {displayCell(row?.qtySold)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Container>
  );
};

export default FastSlowMoving;
